import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, clearSessionToken } from "../lib/api";
import { setBillingCurrency } from "../lib/quickPromote";
import { signOutFirebase } from "../lib/firebase";
import { initPushNotifications } from "../lib/pushNotifications";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // v120 — When the current session is a Super-Admin impersonating another
  // user, we hydrate this so the top-of-app banner can render.
  const [impersonation, setImpersonation] = useState(null);

  // v149.29 — `checkAuth` used to treat ANY failure of `GET /auth/me`
  // (network blip on tab wake, a cold backend restart, a timed-out
  // request, an aborted fetch during a fast route change) exactly like a
  // confirmed invalid session — `setUser(null)` with no retry. Since
  // this runs on every fresh mount of `AuthProvider` (full reload, new
  // tab, wake-from-sleep), a single transient failure at that moment
  // looked to the user like being auto-logged-out, even though the
  // session cookie/token was still perfectly valid. That's the
  // inconsistency the axios interceptor in `lib/api.js` (v144.6/v147.26)
  // already guards against for every OTHER endpoint — only a confirmed
  // "the token is bad" response clears the session — but this one
  // direct call bypassed it.
  //
  // Fix: only a definitive answer ABOUT THE TOKEN ITSELF is trusted to
  // decide auth state.
  //   * 401 (or a 2xx body missing `user_id`) → truly signed out.
  //     Clears the token immediately, same as before.
  //   * Any other definitive 4xx (403 suspended, etc.) → also signed
  //     out, same as the pre-existing behaviour for that class.
  //   * No response at all (network error, timeout, offline, request
  //     aborted mid-flight) OR a 5xx (backend mid-restart, gateway
  //     hiccup) says nothing about whether the token is valid —
  //     inconclusive. Retries once after a short delay; if that retry
  //     is ALSO inconclusive, the screen falls back to signed-out (we
  //     still can't reach the server either way) but the stored
  //     token/cookie is left untouched, so the very next successful
  //     check — no explicit re-login needed — restores the session
  //     automatically.
  const checkAuth = useCallback(async (isRetry = false) => {
    try {
      const { data } = await api.get("/auth/me");
      // Guard: only trust the response if it looks like a real user document
      // (has a `user_id`). Anything else (empty object, string, etc.) is treated
      // as logged-out — prevents phantom "Signed in as ." states caused by
      // stale localStorage tokens pointing at deleted/malformed sessions.
      if (data && typeof data === "object" && data.user_id) {
        // v149.75 — amounts across the app are formatted in the account's
        // billing currency (INR / USD), decided by the server.
        setBillingCurrency(data.currency);
        setUser(data);
        setLoading(false);
        initPushNotifications().catch(() => {});
        // Non-critical: check if we're inside an impersonation session so
        // the banner appears. Failure is silent — a normal login just
        // returns `{impersonating: null}`.
        api.get("/admin/impersonation/current")
          .then(({ data: imp }) => setImpersonation(imp?.impersonating || null))
          .catch(() => setImpersonation(null));
      } else {
        clearSessionToken();
        setUser(null);
        setImpersonation(null);
        setLoading(false);
      }
    } catch (e) {
      const status = e?.response?.status;
      const inconclusive = !status || status >= 500;
      if (inconclusive && !isRetry) {
        // Network error / timeout / offline / 5xx — give the server one
        // more chance before treating this as a logout.
        setTimeout(() => { checkAuth(true); }, 1200);
        return;
      }
      // 401 means the stored token / cookie is invalid — drop it so the
      // next login round-trip mints a fresh one. Any other definitive
      // 4xx (403, etc.) also lands here as signed-out, matching the
      // prior behaviour for those cases. A still-inconclusive result
      // after the retry (persistent network/5xx failure) falls through
      // to here too, but leaves the token alone (no `clearSessionToken`
      // call below) so the session can self-restore later.
      if (status === 401) clearSessionToken();
      setUser(null);
      setImpersonation(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Expose the current user_id on `window` so lightweight globals (e.g. the
    // JS error reporter in `lib/errorReporter.js`) can read it without an
    // import cycle. Safe to leave undefined when signed out.
    if (typeof window !== "undefined") {
      window.__linkolaUserId = user?.user_id || null;
    }
  }, [user]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* backend already unreachable — proceed with local cleanup */ }
    // Purge every trace of the previous session so a subsequent boot can't
    // accidentally re-populate `user` from stale state:
    //   1. Firebase native / web credential (Keychain / IndexedDB).
    //   2. Bearer token in localStorage (our own Capacitor fallback).
    //   3. Any Firebase Auth persistence keys the web SDK left behind.
    await signOutFirebase();
    clearSessionToken();
    try {
      // Firebase web SDK stores per-project keys under `firebase:authUser:<apiKey>:[DEFAULT]`.
      // Wipe anything matching so a stale IdP token doesn't rehydrate silently.
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && (k.startsWith("firebase:") || k === "ref_code")) localStorage.removeItem(k);
      }
    } catch { /* private mode — fine */ }
    setUser(null);
    // Hard reload so no React state / in-flight request survives the logout.
    // `.replace()` (vs `.href`) also clears history so back-nav can't return
    // the user to an authenticated screen.
    // v146.10 — Land on /signin (not /) so returning users can sign
    // straight back in without hunting for the button on the marketing
    // landing page.
    window.location.replace("/signin?mode=login");
  };

  // Alias exposed for the Firebase login button (semantic name)
  const refresh = checkAuth;

  // v120 — Called by the impersonation banner. Ends the current
  // impersonation session server-side, then reloads so the admin's original
  // session cookie takes effect for the next request.
  const exitImpersonation = async () => {
    try {
      await api.post("/admin/impersonate/exit");
    } catch { /* even on failure, do the local reset below */ }
    clearSessionToken();
    setImpersonation(null);
    window.location.replace("/admin");
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, refresh, logout,
                                    impersonation, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
